import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import NavigationBar from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Add } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { AuthContext } from "../../context/authcontext";
import { API_CONFIG } from "../../api/apiUrl";
import { ContractorType, PlotServiceData, TABS, TabType } from "../../types/Booking";

const PAGE_SIZE = 10;

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

function getStatus(tab: TabType): string {
  if (tab === "Request") return "REQUESTED";
  if (tab === "In Progress") return "INPROGRESS";
  if (tab === "Completed") return "COMPLETED";
  return "REQUESTED";
}

function getPageParam(params: URLSearchParams): number {
  const p = parseInt(params.get("page") || "1", 10);
  return isNaN(p) || p < 1 ? 1 : p;
}

function getSearchParam(params: URLSearchParams): string {
  return params.get("search") || "";
}

function setParams(setSearchParams: (fn: (params: URLSearchParams) => URLSearchParams) => void, page: number, search: string, status?: string) {
  setSearchParams((params: URLSearchParams) => {
    const p = new URLSearchParams(params);
    p.set("page", String(page));
    if (search) p.set("search", search); else p.delete("search");
    if (status) p.set("status", status);
    return p;
  });
}

const BookingUI = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const page = getPageParam(searchParams);
  const search = getSearchParam(searchParams);
  const debouncedSearch = useDebounce(search, 500);
  const [activeTab, setActiveTab] = useState<TabType>("Request");
  const [data, setData] = useState<PlotServiceData[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("Auth required");
  const { authorities, setSelectedPlotId } = auth;
  const isProviderOrRM = authorities.includes("ROLE_PROVIDER") || authorities.includes("ROLE_RM");
  const isRM = authorities.includes("ROLE_RM");
  const isProviderOnly = authorities.includes("ROLE_PROVIDER") && !authorities.includes("ROLE_RM");


  useEffect(() => {

    if (location.state && location.state.selectedTab) {
      setActiveTab(location.state.selectedTab);
    } else {
      const status = searchParams.get('status');
      if (!status) {
        const url = new URL(window.location.href);
        url.searchParams.set('status', 'REQUESTED');
        window.history.replaceState({}, '', url.pathname + url.search);
      } else {
        if (status === 'INPROGRESS') setActiveTab('In Progress');
        else if (status === 'COMPLETED') setActiveTab('Completed');
        else setActiveTab('Request');
      }
    }
  }, [location.state, searchParams]);

  const statusParam = searchParams.get('status') || 'REQUESTED';

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError("");
      setErrorDetail("");
      try {
        const token = localStorage.getItem("id_token");
        if (!token) throw new Error("No token");
        const url = `${API_CONFIG.PLOTS}?page=${page - 1}&size=${PAGE_SIZE}&status=${getStatus(activeTab)}&search=${encodeURIComponent(debouncedSearch)}&sort=createDate,desc`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          let body: any = null;
          try { body = await res.json(); } catch { }
          if (res.status === 500 && isProviderOnly && body && body.detail && body.detail.includes("Index 0 out of bounds")) {
            setErrorDetail("No plot is assigned to you yet. Please wait until a plot is assigned.");
            setData([]); setTotal(0); return;
          }
          throw new Error(body?.detail || "API error");
        }
        const json = await res.json();
        const items: PlotServiceData[] = Array.isArray(json) ? json : json?.items || [];
        const countHeader = res.headers.get("X-Total-Count");
        const count = countHeader ? parseInt(countHeader || "0", 10) : json?.totalRecords || items.length;
        setData(items);
        setTotal(count);
      } catch (e: unknown) {
        if (typeof e === 'object' && e !== null && 'message' in e) {
          setError((e as any).message || "Error");
        } else {
          setError("Error");
        }
        setData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [page, activeTab, debouncedSearch, isProviderOnly]);

  function handleTab(tab: TabType) {
    setActiveTab(tab);
    const statusMap: Record<TabType, string> = { Request: "REQUESTED", "In Progress": "INPROGRESS", Completed: "COMPLETED" };
    const status = statusMap[tab];
    setParams(setSearchParams, 1, search, status);
    const url = new URL(window.location.href);
    url.searchParams.set('status', status);
    window.history.replaceState({}, '', url.pathname + url.search);
  }

  function handlePage(newPage: number) {
    setParams(setSearchParams, newPage, search, statusParam);
  }

  function handleSearch(e: any) {
    setParams(setSearchParams, 1, e.target.value);
  }

  function handleRow(plotId: number, contractorType: ContractorType, plotRateCards?: any[]) {
    setSelectedPlotId?.(plotId);
    const plot = data.find(item => item.plot.id === plotId);
    const status = plot ? plot.plot.status : 'REQUESTED';
    navigate(`/customer-requirements/${plotId}?status=${status}`, { state: { selectedService: contractorType, plotRateCards: plotRateCards || [], status } });
  }

  const handleViewClick = (plotId: number, contractorType: ContractorType, companyProfile: { id: number } | null, isRateCardApproved: boolean, plotRateCards?: any[], isBookingPaid?: boolean, isReassigned?: boolean | null) => {
    setSelectedPlotId?.(plotId);
    const plot = data.find(item => item.plot.id === plotId);
    const status = plot ? plot.plot.status : 'REQUESTED';


    if (plot && isReassigned !== false) {
      const updatedData = data.map(item => 
        item.plot.id === plotId 
          ? { ...item, isReassigned: false }
          : item
      );
      setData(updatedData);
    }

    if (isBookingPaid) {
      console.log("Booking is paid, navigating to milestone", { plotId, companyProfile, isBookingPaid });
      if (companyProfile?.id) {
        const companyProfileId = companyProfile.id;
        console.log("Navigating to milestone with company profile", { companyProfileId, plotId });
        navigate(`/milestone/${companyProfileId}?plotId=${plotId}`);
        return;
      } else {
        console.log("No company profile found for paid booking, navigating to customer requirements");
        navigate(`/customer-requirements/${plotId}?status=${status}`, { state: { selectedService: contractorType, plotRateCards: plotRateCards || [], status } });
        return;
      }
    }

    if (!isRateCardApproved) {
      navigate(`/customer-requirements/${plotId}?status=${status}`, { state: { selectedService: contractorType, plotRateCards: plotRateCards || [], status } });
      return;
    }
    if (!companyProfile?.id) {
      navigate(`/customer-requirements/${plotId}?status=${status}`, { state: { selectedService: contractorType, plotRateCards: plotRateCards || [], status } });
      return;
    }
    const companyProfileId = companyProfile.id;
    navigate(`/milestone/${companyProfileId}?plotId=${plotId}`);
  };

  function handleAddPlots() {
    navigate("/");
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const maxButtons = 5;
  let start = Math.max(1, page - Math.floor(maxButtons / 2));
  let end = Math.min(totalPages, start + maxButtons - 1);
  if (end - start + 1 < maxButtons) start = Math.max(1, end - maxButtons + 1);
  const pageRange: number[] = [];
  for (let i = start; i <= end; i++) pageRange.push(i);

  const statusMap: Record<TabType, string> = { Request: "REQUESTED", "In Progress": "INPROGRESS", Completed: "COMPLETED" };
  const filtered: PlotServiceData[] = data.filter(item => item.plot.status === statusMap[activeTab]);

  return (
    <Box sx={{ maxWidth: "2000px", margin: "0 auto" }}>
      <NavigationBar />
      <Box sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 0.8, md: 2, lg: 4, xl: 6 } }}>
        <Box sx={styles.mainContainer}>
          <Box sx={styles.header}>
            <Typography sx={{ fontSize: { xs: "20px", sm: "25px" }, fontWeight: 500 }}>Booking</Typography>
          </Box>
          <Box sx={{ padding: { xs: "8px", sm: "8px 12px", md: "0 45px 45px" } }}>
            <Box sx={styles.tabsContainer}>
              {TABS.map(tab => (
                <Button key={tab} variant={activeTab === tab ? "contained" : "outlined"} onClick={() => handleTab(tab)} sx={styles.tabButton(activeTab === tab)}>{tab}</Button>
              ))}
            </Box>
            <Box>
              <Box sx={{ display: "flex", justifyContent: "end", gap: 2, mb: 2, alignItems: "center" }}>
                {isProviderOrRM && (
                  <TextField
                    placeholder="Search by Name"
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={handleSearch}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', borderColor: '#89652A', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#B69D74', borderWidth: '1.5px' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A', borderWidth: '2px' }, }, '& .MuiInputBase-input': { padding: '10px 10px' }, width: { xs: '100%', sm: '300px' }, }}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#89652A' }} /> }}
                  />
                )}
                {authorities.includes("ROLE_USER") && !authorities.includes("ROLE_PROVIDER") && !isRM && (
                  <Button variant="outlined" startIcon={<Add />} onClick={handleAddPlots} sx={{ borderColor: "#89652A", color: "#89652A", textTransform: "none", borderRadius: "8px", padding: { xs: "6px 12px", sm: "8px 16px" }, fontWeight: 600, background: "linear-gradient(135deg, #fff 0%, #F5F5F5 100%)", "&:hover": { borderColor: "#B69D74", color: "#B69D74", background: "#fff" } }}>Add More Plots</Button>
                )}
              </Box>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                  <CircularProgress sx={{ color: "#89652A" }} />
                  <Typography sx={{ ml: 2 }}>Loading, please wait...</Typography>
                </Box>
              ) : error ? (
                <Box sx={{ textAlign: "center", py: 4, color: "error.main" }}>
                  <Typography>Error: {error}</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer sx={styles.tableContainer}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["Name", "Plot Size", "Location", "Status", "Facing", "Floors", "Rooms"].map(header => (
                            <TableCell key={header} sx={styles.tableCellHeader}>{header}</TableCell>
                          ))}
                          {isRM && <TableCell sx={styles.tableCellHeader}>Action</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {errorDetail && isProviderOnly ? (
                          <TableRow>
                            <TableCell colSpan={isRM ? 8 : 7} sx={{ textAlign: "center", color: "#B69D74", fontWeight: 600, fontSize: "16px" }}>{errorDetail}</TableCell>
                          </TableRow>
                        ) : filtered.length > 0 ? (
                          filtered.map((item, idx) => {
                            const isRowClickable = !isRM;
                            return (
                              <TableRow
                                key={item.plot.id || idx}
                                {...(isRowClickable && { onClick: () => handleRow(item.plot.id, item.plot.contractorTypes, item.plotRateCards) })}
                                sx={{
                                  ...styles.tableRow,
                                  ...(isRowClickable ? { cursor: 'pointer' } : { cursor: 'default' })
                                }}
                              >
                                <TableCell sx={styles.tableCell}>{item.plot.plotOwner?.user?.firstName ?? item.name ?? "Unknown"}</TableCell>
                                <TableCell sx={styles.tableCell}>{item.plot.plotSize} sq</TableCell>
                                <TableCell sx={styles.tableCell}>{item.plot.plotLocation}</TableCell>
                                <TableCell sx={styles.tableCell}>{item.plot.status ? item.plot.status.charAt(0).toUpperCase() + item.plot.status.slice(1).toLowerCase() : "Pending"}</TableCell>
                                <TableCell sx={styles.tableCell}>{item.plot.plotFacing}</TableCell>
                                <TableCell sx={styles.tableCell}>{item.plot.plotFloors}</TableCell>
                                <TableCell sx={styles.tableCell}>{item.plot.plotRooms}</TableCell>
                                {isRM && (
                                  <TableCell sx={styles.tableCell}>
                                    {item.isBookingPaid === true ? (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewClick(item.plot.id, item.plot.contractorTypes, item.companyProfile, item.isRateCardApproved, item.plotRateCards, item.isBookingPaid, item.isReassigned);
                                        }}
                                        sx={{
                                          width: '75px',
                                          borderColor: '#89652A',
                                          color: '#89652A',
                                          borderRadius: '8px',
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          minWidth: '70px',
                                          padding: '4px 8px',
                                          fontSize: { xs: '12px', sm: '13px' },
                                          boxShadow: 'none',
                                          background: 'transparent',
                                          '&:hover': {
                                            borderColor: '#B69D74',
                                            color: '#B69D74',
                                            background: 'transparent',
                                            opacity: 0.85
                                          }
                                        }}
                                      >
                                        View
                                      </Button>
                                    ) : item.isRateCardApproved === true ? (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewClick(item.plot.id, item.plot.contractorTypes, item.companyProfile, item.isRateCardApproved, item.plotRateCards, item.isBookingPaid, item.isReassigned);
                                        }}
                                        sx={{
                                          width: '75px',
                                          borderColor: '#89652A',
                                          color: '#89652A',
                                          borderRadius: '8px',
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          minWidth: '70px',
                                          padding: '4px 8px',
                                          fontSize: { xs: '12px', sm: '13px' },
                                          boxShadow: 'none',
                                          background: 'transparent',
                                          '&:hover': {
                                            borderColor: '#B69D74',
                                            color: '#B69D74',
                                            background: 'transparent',
                                            opacity: 0.85
                                          }
                                        }}
                                      >
                                        View
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewClick(item.plot.id, item.plot.contractorTypes, item.companyProfile, item.isRateCardApproved, item.plotRateCards, item.isBookingPaid, item.isReassigned);
                                        }}
                                        sx={{
                                          borderColor: '#89652A',
                                          color: 'white',
                                          borderRadius: '8px',
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          minWidth: '70px',
                                          padding: '4px 8px',
                                          fontSize: { xs: '12px', sm: '13px' },
                                          boxShadow: 'none',
                                          background: '#89652A',
                                        }}
                                      >
                                        {item.isReassigned === false ? 'Re-assign' : 'Assign'}
                                      </Button>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={isRM ? 8 : 7} sx={{ textAlign: "center" }}>{search ? "No plots match your search" : `No plots available for ${activeTab}`}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1, gap: 1, alignItems: "center", mt: 2 }}>
                    <Button variant="contained" size="small" sx={{ backgroundColor: "#89652A", "&:hover": { backgroundColor: "#B69D74" }, minWidth: "10px" }} onClick={() => handlePage(page - 1)} disabled={page === 1 || loading}><ChevronLeft /></Button>
                    {pageRange.map(num => (
                      <Button key={num} variant={num === page ? "contained" : "outlined"} size="small" sx={{ minWidth: "35px", border: '1px solid #89652A', backgroundColor: num === page ? "#89652A" : "white", color: num === page ? "white" : "#89652A", "&:hover": { backgroundColor: num === page ? "#89652A" : "#f5f5f5" } }} onClick={() => handlePage(num)} disabled={loading}>{num}</Button>
                    ))}
                    {totalPages > maxButtons && page < totalPages - maxButtons + 1 && (
                      <Typography sx={{ mx: 1 }}>...</Typography>
                    )}
                    <Button variant="contained" size="small" sx={{ backgroundColor: "#89652A", "&:hover": { backgroundColor: "#B69D74" }, minWidth: "10px" }} onClick={() => handlePage(page + 1)} disabled={page >= totalPages || loading}><ChevronRight /></Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

const styles = {
  mainContainer: {
    borderRadius: { xs: "10px", sm: "10px" },
    boxShadow: "0px 0px 3px 0px #00000033",
  },
  header: {
    background: "linear-gradient(90deg, #89652A 0%, #B69D74 100%)",
    color: "#fff",
    padding: { xs: "10px 12px", sm: "14px 20px", md: "16px 24px" },
    borderTopLeftRadius: { xs: "6px", sm: "8px" },
    borderTopRightRadius: { xs: "6px", sm: "8px" },
  },
  tabsContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: { xs: "center", sm: "flex-end" },
    padding: { xs: "12px 6px", sm: "12px 16px", md: "16px 24px" },
    gap: { xs: "4px", sm: "10px", md: "18px" },
    flexWrap: "wrap",
  },
  tabButton: (isActive: boolean) => ({
    backgroundColor: isActive ? "#89652A" : "transparent",
    color: isActive ? "#fff" : "#000",
    border: isActive ? "none" : "1px solid #E5E5E5",
    minWidth: { xs: "100px", sm: "120px" },
    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" },
    fontWeight: 500,
    padding: { xs: "4px 10px", sm: "5px 24px", md: "5px 30px" },
    "&:hover": { backgroundColor: isActive ? "#89652A" : "#F5F5F5" },
    textTransform: "none",
    borderRadius: "100px",
    "&.Mui-disabled": { color: "gray", border: "1px solid #E5E5E5", backgroundColor: "#f5f5f5" },
  }),
  tableContainer: {
    boxShadow: "none",
    borderRadius: "8px",
    width: "99%",
    margin: "auto",
    border: "0.75px solid #2D2D2D33",
    overflowX: "auto",
    "&::-webkit-scrollbar": { height: "6px" },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#89652A55", borderRadius: "3px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "#F5F5F5", borderRadius: "3px" },
  },
  tableCell: {
    padding: { xs: "10px", sm: "12px", md: "14px" },
    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" },
    fontWeight: 400,
    textAlign: "center",
    whiteSpace: "nowrap",
    minWidth: { xs: "100px", sm: "130px" },
    textTransform: "capitalize",
  },
  tableCellHeader: {
    padding: { xs: "10px", sm: "12px", md: "14px" },
    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" },
    textAlign: "center",
    fontWeight: 600,
    backgroundColor: "#A3A3A333",
    whiteSpace: "nowrap",
    minWidth: { xs: "100px", sm: "130px" },
  },
  tableRow: {
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
};

export default BookingUI;
